


import { useState, useContext } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as Download } from "bootstrap-icons/icons/download.svg";
import { ReactComponent as Search } from "bootstrap-icons/icons/search.svg";
import { ReactComponent as ArrowClockwise } from "bootstrap-icons/icons/arrow-clockwise.svg";
import { ReactComponent as XCircle } from "bootstrap-icons/icons/x-circle.svg";
import Spinner from "../../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../../components/utils/CustomStyles";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import AxiosApiCall from "../../../api/AxiosApiCall";
import { AuthContext } from "../../../AuthProvider/AuthProvider";
import ApiServices from "../../../api/ApiServices";
import formatDate from "../../../components/utils/DateFormatter";
import { downloadCSVFromApi } from "../../../components/utils/DownlaodCSVFromApi";
import { useBranchStockMonitoring, useBranches, useBranchProducts } from "../../../hooks/useAppData";

const BranchStockMonitoring = () => {
    const [loadingCSV, setLoadingCSV] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const { permissions } = useContext(AuthContext);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [clearFilterLoading, setClearFilterLoading] = useState(false);

    const defaultFilters = {
        per_page: 20,
        page: 1,
        branch_id: "",
        product_id: "",
        start_date: "",
        end_date: "",
    };

    const [filters, setFilters] = useState(defaultFilters);

    const navigate = useNavigate();
    const axiosInst = AxiosApiCall();

    const { data: branchesResponse } = useBranches();
    const branchOpe = (branchesResponse?.data || []).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const { data: branchProductsResponse } = useBranchProducts("");
    const rawBranchPro = branchProductsResponse?.data?.data || branchProductsResponse?.data || [];
    const branchPro = Array.isArray(rawBranchPro) ? rawBranchPro.filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i) : [];

    const stockFilters = {
        ...filters,
        branch_id: filters.branch_id ? Number(filters.branch_id) : "",
        product_id: filters.product_id ? Number(filters.product_id) : "",
    };
    const { data: stockResponse, isLoading: loading } = useBranchStockMonitoring(stockFilters);
    const branchStock = Array.isArray(stockResponse?.data?.snapshots)
        ? stockResponse.data.snapshots.slice().sort((a, b) => new Date(b.snapshot_date) - new Date(a.snapshot_date))
        : [];
    const pagination = {
        total: stockResponse?.data?.total || 0,
        currentPage: stockResponse?.data?.current_page || 1,
        perPage: stockResponse?.data?.per_page || 20,
    };

    // Check if any filter is applied (excluding default values)
    const isAnyFilterFilled = () => {
        const { per_page, page, ...filterFields } = filters;
        return Object.values(filterFields).some(
            (value) => value !== "" && value !== null
        );
    };

    // Clear all filters including dates and reset to default
    const handleClearFilters = () => {
        setClearFilterLoading(true);
        setFilterText("");
        setFilters(defaultFilters);
        toast.success("All filters cleared");
        setClearFilterLoading(false);
    };

    // Clear only date filters
    const handleClearDates = () => {
        setFilters(prev => ({
            ...prev,
            start_date: "",
            end_date: ""
        }));
        toast.success("Date filters cleared");
    };

    const handleFilterClick = () => {
        if (!isAnyFilterFilled()) {
            toast.warning("Please select at least one filter!");
            return;
        }
        // React Query auto re-fetches when filters state changes (which updates stockFilters)
    };

    // Check if both dates are selected
    const areDatesSelected = filters.start_date && filters.end_date;

    // Handle CSV Download
    const handleDownloadCSV = async () => {
        if (!areDatesSelected) {
            toast.error("Please select both start and end dates to download CSV");
            return;
        }

        const startDate = new Date(filters.start_date);
        const endDate = new Date(filters.end_date);

        if (startDate > endDate) {
            toast.error("Start date must be before end date");
            return;
        }

        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        if (daysDiff > 365) {
            toast.error("Date range cannot exceed 1 year");
            return;
        }

        setLoadingCSV(true);

        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;
            if (!token) { toast.error("No access token found"); return; }

            const downloadPayload = {
                start_date: filters.start_date,
                end_date: filters.end_date,
                download: 1,
            };

            const startDateFormatted = filters.start_date.replace(/-/g, '_');
            const endDateFormatted = filters.end_date.replace(/-/g, '_');
            const filename = `branch_stock_monitoring_${startDateFormatted}_to_${endDateFormatted}.csv`;

            await downloadCSVFromApi(
                ApiServices.branchStockMonitoring,
                downloadPayload,
                token,
                {
                    filename: filename,
                    columnsToRemove: [],
                    maxRows: 50000,
                    method: "POST",
                    useBearerToken: true
                }
            );

        } catch (error) {
            // console.error("Download error:", error);
            handleError(error, navigate);
            if (error.message && error.message.includes("CSV")) {
                toast.error("Failed to process CSV file");
            } else if (error.message && error.message.includes("network")) {
                toast.error("Network error during download");
            }
        } finally {
            setLoadingCSV(false);
        }
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    // Filter data based on search text and date range
    const filteredItems = branchStock.filter((item) => {
        // Filter by search text
        const matchesFilterText =
            item.product_name?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.total_purchase_amount.includes(filterText) ||
            item.snapshot_date.includes(filterText) ||
            item.opening_stock.toString().includes(filterText) ||
            item.closing_stock.toString().includes(filterText) ||
            item.total_sales_amount.includes(filterText) ||
            item.product_category?.toLowerCase().includes(filterText.toLowerCase());

        // Filter by date range (if dates are selected)
        let matchesDateRange = true;
        if (areDatesSelected) {
            const itemDate = new Date(item.snapshot_date || item.created_at);
            const startDate = new Date(filters.start_date);
            const endDate = new Date(filters.end_date);

            endDate.setHours(23, 59, 59, 999);
            matchesDateRange = itemDate >= startDate && itemDate <= endDate;
        }

        return matchesFilterText && matchesDateRange;
    });

    const columns = [
        {
            name: "Date",
            selector: (row) => formatDate(row.snapshot_date),
            sortable: true,
            width: "200px",
        },
        {
            name: "Branch Name",
            selector: (row) =>
                row.branch_name
                    ? row.branch_name.replace(/_/g, " ").toUpperCase()
                    : "NOT AVAILABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Product",
            selector: (row) =>
                row.product_name
                    ? row.product_name.replace(/_/g, " ").toUpperCase()
                    : "NOT AVAILABLE",
            sortable: true,
            width: "220px",
        },
        {
            name: "Product Category",
            selector: (row) =>
                row.product_category
                    ? row.product_category.replace(/_/g, " ").toUpperCase()
                    : "NOT AVAILABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Opening Stock",
            selector: (row) => row.opening_stock ? row.opening_stock : "0",
            wrap: true,
            width: "200px",
        },
        {
            name: "Closing Stock",
            selector: (row) => row.closing_stock ? row.closing_stock : "0",
            wrap: true,
            width: "200px",
        },
        {
            name: "Current Stock",
            selector: (row) => row.current_stock ? row.current_stock : "0",
            wrap: true,
            width: "200px",
        },
        {
            name: "Total Purchase Amount",
            selector: (row) => CurrencyFormatter(row.total_purchase_amount || 0),
            sortable: true,
            width: "200px",
        },
        {
            name: "Total Sales Amount",
            selector: (row) => CurrencyFormatter(row.total_sales_amount || 0),
            sortable: true,
            width: "200px",
        },
    ];

    return (
        <MainLayouts>
            <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                <section
                    className="w-full pr-[3%] pt-3 my-3"
                    style={{ backgroundColor: "#F7F7F7" }}
                >
                    <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                        <ol className="list-none p-0 inline-flex mb-2">
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                    Operations
                                </p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                    Stock
                                </p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-orange1">Monitoring</p>
                            </li>
                        </ol>
                    </nav>
                </section>

                {/* Filter Controls Section */}
                <div className="pb-4 bg-white dark:bg-gray-900">
                    {/* DESKTOP FILTERS */}
                    <div className="hidden md:flex flex-wrap items-center gap-3">
                        {/* Search Input */}
                        <div className="relative flex-grow min-w-[200px]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="search"
                                    className="block w-full pl-10 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                    placeholder="Search ..."
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Clear Dates Button */}
                        {areDatesSelected && (
                            <div className="min-w-[120px]">
                                <button
                                    onClick={handleClearDates}
                                    className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                                >
                                    <XCircle className="w-4 h-4" />
                                    Clear Dates
                                </button>
                            </div>
                        )}

                        {/* Download Button */}
                        <div className="min-w-[160px]">
                            <button
                                onClick={handleDownloadCSV}
                                className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${areDatesSelected
                                    ? "bg-orange1 text-lGrey3 hover:bg-orange-600 cursor-pointer"
                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    }`}
                                disabled={!areDatesSelected || loadingCSV}
                                title={!areDatesSelected ? "Select start and end dates to enable download" : ""}
                            >
                                {loadingCSV ? (
                                    <>
                                        <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-4 h-4" />
                                        Download CSV
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* MOBILE FILTERS */}
                    <div className="md:hidden flex flex-col gap-3">
                        <div className="w-full">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="search"
                                    className="block w-full pl-10 pr-3 py-3 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                                    placeholder="Search ..."
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            {areDatesSelected && (
                                <div className="col-span-2">
                                    <button
                                        onClick={handleClearDates}
                                        className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-3 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800"
                                    >
                                        <XCircle className="w-4 h-4" />
                                        Clear Dates
                                    </button>
                                </div>
                            )}

                            <div className="col-span-2">
                                <button
                                    onClick={handleDownloadCSV}
                                    className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-3 text-sm font-semibold ${areDatesSelected
                                        ? "bg-orange1 text-lGrey3 hover:bg-orange-600"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                    disabled={!areDatesSelected || loadingCSV}
                                >
                                    {loadingCSV ? (
                                        <>
                                            <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                            <span className="ml-2">Downloading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4" />
                                            <span className="ml-2">Download CSV</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Help Text */}
                    <div className="hidden md:flex items-center justify-between mt-2">
                        {!areDatesSelected && (
                            <p className="text-xs text-gray-500">
                                Select start and end dates to enable download
                            </p>
                        )}
                        {areDatesSelected && filteredItems.length > 0 && (
                            <p className="text-sm text-gray-600">
                                Showing {filteredItems.length} records from {filters.start_date} to {filters.end_date}
                            </p>
                        )}
                    </div>

                    <div className="md:hidden mt-3">
                        {!areDatesSelected && (
                            <p className="text-xs text-gray-500 text-center mb-2">
                                Select start and end dates to enable download
                            </p>
                        )}
                        {areDatesSelected && filteredItems.length > 0 && (
                            <p className="text-sm text-gray-600 text-center">
                                Showing {filteredItems.length} records
                            </p>
                        )}
                    </div>
                </div>

                {/* Advanced Filters Section */}
                <div className="pb-4 bg-white dark:bg-gray-900">
                    <div className="flex flex-wrap gap-3 mb-4 w-full">
                        <select
                            className="border rounded px-3 py-2 border-lGrey w-full sm:w-[220px]"
                            value={filters.branch_id}
                            onChange={(e) =>
                                setFilters({ ...filters, branch_id: e.target.value })
                            }
                        >
                            <option value=""> -- Select Branch -- </option>
                            {branchOpe.map((branch) => (
                                <option key={branch.id} value={branch.id}>
                                    {branch.name}
                                </option>
                            ))}
                        </select>

                        {/* Product dropdown - now available regardless of branch selection */}
                        <select
                            className="border rounded px-3 py-2 border-lGrey w-full sm:w-[220px]"
                            value={filters.product_id}
                            onChange={(e) =>
                                setFilters({ ...filters, product_id: e.target.value })
                            }
                        >
                            <option value=""> -- Select Product -- </option>
                            {branchPro.map((product) => (
                                <option key={product.id} value={product.id}>
                                    {(product.product_units_name).replace(/_/g, " ") || product.name || `Product ${product.id}`}
                                </option>
                            ))}
                        </select>

                        <input
                            type="date"
                            className="border rounded px-3 py-2 border-lGrey w-full sm:w-[220px]"
                            value={filters.start_date}
                            onChange={(e) =>
                                setFilters({ ...filters, start_date: e.target.value })
                            }
                            max={filters.end_date || undefined}
                        />
                        <input
                            type="date"
                            className="border rounded px-3 py-2 border-lGrey w-full sm:w-[220px]"
                            value={filters.end_date}
                            onChange={(e) =>
                                setFilters({ ...filters, end_date: e.target.value })
                            }
                            min={filters.start_date || undefined}
                        />

                        <div className="flex gap-2 w-full sm:w-auto">
                            <button
                                onClick={handleFilterClick}
                                className="bg-darkGreen3 text-lGrey px-4 py-2 rounded disabled:opacity-50 w-full sm:w-auto"
                                disabled={!isAnyFilterFilled() || filterLoading}
                            >
                                {filterLoading ? "Filtering..." : "Apply Filters"}
                            </button>

                            {isAnyFilterFilled() && (
                                <button
                                    onClick={handleClearFilters}
                                    className="bg-orange1 text-lGrey px-4 py-2 rounded hover:bg-orange-600 transition-colors w-full sm:w-auto"
                                    disabled={clearFilterLoading}
                                >
                                    {clearFilterLoading ? "Clearing..." : "Clear All Filters"}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filter Status Indicator */}
                    {/* {isAnyFilterFilled() && (
                        <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium text-blue-700">Active Filters:</span>
                                {filters.branch_id && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                        Branch: {branchOpe.find(b => b.id == filters.branch_id)?.name}
                                    </span>
                                )}
                                {filters.product_id && (
                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                        Product: {branchPro.find(p => p.id == filters.product_id)?.product_units_name}
                                    </span>
                                )}
                                {filters.start_date && (
                                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                                        From: {filters.start_date}
                                    </span>
                                )}
                                {filters.end_date && (
                                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                                        To: {filters.end_date}
                                    </span>
                                )}
                            </div>
                        </div>
                    )} */}
                </div>

                {loading ? (
                    <Spinner />
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        pagination
                        highlightOnHover
                        striped
                        customStyles={CustomStyles}
                        noDataComponent={
                            <div className="py-8 text-center text-gray-500">
                                {isAnyFilterFilled()
                                    ? "No stock records found with the selected filters"
                                    : "No branch stock records found"}
                            </div>
                        }
                    />
                )}
            </div>
        </MainLayouts>
    );
};

export default BranchStockMonitoring;
